/**
 * Builds A4-formatted abstract preview HTML and renders it into the target element.
 *
 * @param {HTMLElement} previewEl - The target element to render into (#abstract-preview)
 * @param {Object} data - Preview data object
 * @param {string} data.title
 * @param {string} data.mainName
 * @param {string} data.mainInst
 * @param {string} data.presName
 * @param {string} data.presInst
 * @param {string} data.presEmail
 * @param {Array<{name: string, institution: string}>} data.coauthors
 * @param {string} data.bodyHtml - HTML string for abstract body (Quill innerHTML or stored HTML)
 * @param {Array<string>} data.keywords
 * @param {Array<string>} data.references
 */
export function buildAbstractPreview(previewEl, data) {
  if (!previewEl) return;

  const title     = data.title     || '';
  const mainName  = data.mainName  || '';
  const mainInst  = data.mainInst  || '';
  const presName  = data.presName  || '';
  const presInst  = data.presInst  || '';
  const presEmail = data.presEmail || '';
  const coauthors = data.coauthors || [];
  const bodyHtml  = data.bodyHtml  || '';
  const keywords  = (data.keywords  || []).filter(k => k.trim().length > 0);
  const references = (data.references || []).filter(r => r.trim().length > 0);

  // ── Build institution index (letter superscripts) ──────────────────────────
  const instList = [];
  function instLetter(inst) {
    if (!inst) return '';
    const idx = instList.indexOf(inst);
    if (idx !== -1) return String.fromCharCode(97 + idx);
    instList.push(inst);
    return String.fromCharCode(97 + instList.length - 1);
  }

  // Pre-pass to assign letters in display order:
  // main author first, then presenter, then coauthors
  const mainLetter     = mainInst ? instLetter(mainInst) : '';
  const presLetter     = presInst ? instLetter(presInst) : '';
  const coauthorLetters = coauthors.map(c => c.institution ? instLetter(c.institution) : '');

  // ── Title section ──────────────────────────────────────────────────────────
  let html = '';
  html += '<div style="text-align:center !important;font-family:\'Times New Roman\',Times,serif !important;font-size:14pt !important;font-weight:bold !important;text-transform:uppercase !important;margin-bottom:12px !important;">';
  html += escHtml(title) || '&nbsp;';
  html += '</div>';

  // ── Authors section ────────────────────────────────────────────────────────
  html += '<div style="text-align:center !important;font-family:\'Times New Roman\',Times,serif !important;font-size:12pt !important;margin-bottom:6px !important;">';

  const authorParts = [];

  if (mainName) {
    const sup = mainLetter ? '<sup style="font-size:8pt !important;">' + escHtml(mainLetter) + '</sup>' : '';
    const isDifferentFromPresenter = mainName !== presName;
    if (isDifferentFromPresenter) {
      authorParts.push('<span style="text-decoration:underline !important;">' + escHtml(mainName) + '</span>' + sup);
    } else {
      authorParts.push(escHtml(mainName) + sup);
    }
  }

  if (presName && presName !== mainName) {
    const sup = presLetter ? '<sup style="font-size:8pt !important;">' + escHtml(presLetter) + '</sup>' : '';
    authorParts.push(escHtml(presName) + sup + '<sup style="font-size:8pt !important;">*</sup>');
  } else if (presName && presName === mainName) {
    if (authorParts.length > 0) {
      authorParts[0] += '<sup style="font-size:8pt !important;">*</sup>';
    }
  }

  coauthors.forEach((c, i) => {
    if (!c.name) return;
    const sup = coauthorLetters[i] ? '<sup style="font-size:8pt !important;">' + escHtml(coauthorLetters[i]) + '</sup>' : '';
    authorParts.push(escHtml(c.name) + sup);
  });

  html += authorParts.join(', ');
  if (!authorParts.length) html += '&nbsp;';
  html += '</div>';

  // ── Institutions section ───────────────────────────────────────────────────
  if (instList.length > 0) {
    html += '<div style="text-align:center !important;font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;font-style:italic !important;color:#6b7280 !important;margin-bottom:4px !important;">';
    const instParts = instList.map((inst, i) => {
      const letter = String.fromCharCode(97 + i);
      return '<sup style="font-size:8pt !important;">' + letter + '</sup>' + escHtml(inst);
    });
    html += instParts.join('; ');
    html += '</div>';
  }

  // ── Corresponding author ───────────────────────────────────────────────────
  if (presEmail) {
    html += '<div style="text-align:center !important;font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;font-style:italic !important;color:#6b7280 !important;margin-bottom:16px !important;">';
    html += 'Corresponding Author: ' + escHtml(presEmail);
    html += '</div>';
  } else {
    html += '<div style="margin-bottom:16px !important;"></div>';
  }

  // ── Divider ────────────────────────────────────────────────────────────────
  html += '<hr style="border:none !important;border-top:1px solid #d1d5db !important;margin-bottom:16px !important;height:0 !important;opacity:1 !important;color:inherit !important;background-color:transparent !important;">';

  // ── Abstract body ──────────────────────────────────────────────────────────
  const sectionDefs = [
    { key: 'introduction', label: 'Introduction' },
    { key: 'case_report',  label: 'Case Report' },
    { key: 'discussion',   label: 'Discussion' },
    { key: 'conclusion',   label: 'Conclusion' }
  ];

  const sectionRegex = /<!--SECTION:(\w+)-->/g;
  const hasSections = sectionRegex.test(bodyHtml);

  if (hasSections) {
    // Parse sections from markers
    const sections = {};
    const parts = bodyHtml.split(/<!--SECTION:(\w+)-->/);
    // parts = ['', 'introduction', 'intro html', 'case_report', 'case html', ...]
    for (let i = 1; i < parts.length; i += 2) {
      sections[parts[i]] = parts[i + 1] || '';
    }

    html += '<div style="font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;">';
    sectionDefs.forEach(function(sec) {
      const content = sections[sec.key] || '';
      const isEmpty = !content || content === '<p><br></p>' || content.trim() === '';
      html += '<div style="margin-top:10px !important;">';
      html += '<p style="text-align:justify !important;margin:0 !important;"><strong>' + escHtml(sec.label) + '</strong></p>';
      if (!isEmpty) {
        html += '<div style="text-align:justify !important;">' + content + '</div>';
      }
      html += '</div>';
    });
    html += '</div>';
  } else {
    // Legacy: single body without sections
    html += '<div style="font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;">';
    html += '<div style="text-align:justify !important;margin-top:6px !important;">';
    html += (bodyHtml === '<p><br></p>' || bodyHtml === '') ? '' : bodyHtml;
    html += '</div>';
    html += '</div>';
  }

  // ── Keywords section ───────────────────────────────────────────────────────
  if (keywords.length > 0) {
    html += '<div style="font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;margin-top:12px !important;text-align:left !important;">';
    html += '<strong>Keywords:</strong> ' + keywords.map(escHtml).join(', ');
    html += '</div>';
  }

  // ── References section ─────────────────────────────────────────────────────
  if (references.length > 0) {
    html += '<div style="font-family:\'Times New Roman\',Times,serif !important;font-size:11pt !important;margin-top:12px !important;text-align:left !important;">';
    html += '<strong>References:</strong>';
    html += '<ol style="margin:4px 0 0 0 !important;padding-left:20px !important;list-style-type:decimal !important;">';
    references.forEach(ref => {
      html += '<li style="margin-bottom:2px !important;">' + escHtml(ref) + '</li>';
    });
    html += '</ol>';
    html += '</div>';
  }

  previewEl.innerHTML = html;
}

// ── Private helper ─────────────────────────────────────────────────────────
function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
